import { Navigate, Route, useLocation } from 'react-router-dom';
import { apiDocsPlugin } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
// Provides the catalog-graph routeRef that EntityCatalogGraphCard resolves — must stay imported+routed.
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
// Custom scaffolder fields (Doc Author Starter Kit) — eager: ScaffolderFieldExtensions reads its
// children's component data at render, so these cannot be lazy.
import {
  AuthorProfileFieldExtension,
  AdoRepoTargetFieldExtension,
  DocTypePickerFieldExtension,
} from './components/scaffolder/fields';
import { orgPlugin } from '@backstage/plugin-org';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { TechDocsResponsiveStyles, TechDocsCopyPermalink, TechDocsDownloadButton, TechDocsAuthorTag, TechDocsSourceLink, TechDocsPreviewBanner } from './components/techdocs/techDocsAddons';
import { TechDocsLanguageProxy } from './components/techdocs/TechDocsLanguageProxy';
import { UserSettingsPage, SettingsLayout } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
// ISDP CUSTOM: CatalogTablePage stays EAGER — attachComponentData() is called on it
// below (binds the catalogIndex routeRef, like a routable extension). Wrapping it in
// lazy() would hide the component `.type` that route discovery reads. Keep eager.
import { CatalogTablePage } from './components/catalog/CatalogTablePage';
import { Root } from './components/Root';
// ISDP CUSTOM: canonical route path strings — single source of truth (see
// components/common/constants/routes.ts). Route paths below reference ROUTES
// instead of hardcoded literals; parameterised/sub-route patterns are composed
// from the matching base constant so every path resolves identically.
import { ROUTES } from './components/common';

import {
  AlertDisplay,
  OAuthRequestDialog,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
// ISDP CUSTOM: HomePage stays EAGER — it's the internal hub's first-paint route.
import { HomePage } from './components/home/HomePage';
// NotFoundPage stays EAGER — it's referenced in createApp({ components }) below, which
// is evaluated at module load, so it can't be lazy.
import { NotFoundPage } from './components/ErrorPage/NotFoundPage';
import { backstageTranslationResources } from './i18n/backstageTranslations';
import { ToastProvider } from './components/feedback/ToastProvider';
import { DevHubErrorBoundary } from './components/common/DevHubErrorBoundary';
import { ConfirmProvider } from './components/feedback/ConfirmProvider';
import { LandscapePage } from '@internal/plugin-landscape';
import {
  isdpAdminPagePermission,
  featureEnabled,
} from '@internal/plugin-isdp-common';
// ISDP CUSTOM: Admin pages are routable extensions — they MUST be imported eagerly.
// Backstage discovers a routable extension's routeRef by traversing the app element
// tree for data attached to the component's `type`; React.lazy hides that data, so
// wrapping these in lazy() breaks route discovery ("routable extension … was not
// discovered"). Code-splitting is preserved: createRoutableExtension already loads
// each page's heavy body via its own internal import() — the eager import here only
// pulls in the thin routeRef wrapper.
import {
  AnalyticsPage,
  CurationHubPage,
  AccessManagementPage,
} from '@internal/plugin-admin';
// ISDP CUSTOM: role-based app gating + MCP personal access tokens
import { useUserRole } from './components/auth/useUserRole';
import { useScrollRestoration } from './hooks/useScrollRestoration';
import { RoleSplash, NoInternalAccess } from './components/auth/RoleGuard';
import { McpTokenSettingsTab } from './components/mcp/McpTokenSettingsTab';
import { DevHubSettingsGeneral } from './components/settings/DevHubSettingsGeneral';
// ISDP CUSTOM: admin-only Resource Monitor (container/DB health). Plain page component.
import { ResourceMonitorPage } from './components/admin/ResourceMonitorPage';
// ISDP CUSTOM: DevSecOps auth helpers stay eager (they render outside/wrapping routes
// and must be available immediately). The hub page components are lazily loaded —
// the hub is a secondary workflow not visited on first load.
import { DevSecOpsAuthGate } from './components/devsecops/DevSecOpsAuthGate';
import { DevSecOpsAuthCallback } from './components/devsecops/DevSecOpsAuthCallback';
import { appThemes } from './appThemes';
import {
  attachComponentData,
  configApiRef,
  useApi,
  type SignInPageProps,
} from '@backstage/core-plugin-api';
import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { SentryInit } from './components/Root/SentryInit';
// ISDP CUSTOM: Premium Dräger-branded sign-in screen
import {
  DraegerSignInPage,
  usesGuestAutoSignIn,
} from './components/SignInPage/SignInPage';
import {
  clearSignedIn,
  isSignedInFlagSet,
} from './components/auth/portalSession';
import { SIGN_IN_PARAM } from './components/public/publicHelpers';

// ISDP CUSTOM: lazily-loaded route chunks. Admin pages (admin-only, never first-loaded)
// and the DevSecOps hub (a secondary workflow) are split out to shrink the initial
// bundle; they load on demand behind the Suspense boundary around the routes.
const DevSecOpsHubPage = lazy(() => import('./components/devsecops/DevSecOpsHubPage').then(m => ({ default: m.DevSecOpsHubPage })));
const HubShell = lazy(() => import('./components/devsecops/DevSecOpsHubPage').then(m => ({ default: m.HubShell })));
const DocReadyPage = lazy(() => import('./components/scaffolder/DocReadyPage').then(m => ({ default: m.DocReadyPage })));
const CreateTemplateList = lazy(() => import('./components/devsecops/SelfServiceCreate').then(m => ({ default: m.CreateTemplateList })));
const ProjectDetailPage = lazy(() => import('./components/devsecops/ProjectDetailPage').then(m => ({ default: m.ProjectDetailPage })));
const ModuleInstancePage = lazy(() => import('./components/devsecops/ModuleInstancePage').then(m => ({ default: m.ModuleInstancePage })));

// ISDP CUSTOM: The external (partner/public) world is one of two mutually-exclusive
// trees — internal users never load it and partners never load the internal routes,
// so splitting it keeps the other world's code out of the initial chunk entirely.
// It is a plain page component (NOT a routable extension), safe to lazy-load. AppBody
// renders it in the partner/public branch wrapped in its own <Suspense> (the routes'
// Suspense only covers the internal <Root> branch).
const ExternalWorld = lazy(() => import('./components/public/ExternalWorld').then(m => ({ default: m.ExternalWorld })));

// ISDP CUSTOM: Internal section/hub/catalog/docs pages that aren't part of first paint.
// All are plain page components rendered directly as a <Route element> under the shared
// Suspense boundary — no routable-extension routeRef discovery is involved, so lazy() is
// safe and behaviour is unchanged (same component, same route; only chunk timing shifts).
// Kept EAGER (see imports above): HomePage (first paint), CatalogTablePage
// (attachComponentData routeRef bind), NotFoundPage (used in createApp components),
// and the admin routable extensions.
const CatalogCardPage = lazy(() => import('./components/catalog/CatalogPage').then(m => ({ default: m.CatalogCardPage })));
const ApiCardPage = lazy(() => import('./components/api/ApiCardPage').then(m => ({ default: m.ApiCardPage })));
const DocsPage = lazy(() => import('./components/docs/DocsPage').then(m => ({ default: m.DocsPage })));
const CuratedDocsPage = lazy(() => import('./components/docs/CuratedDocsPage').then(m => ({ default: m.CuratedDocsPage })));
const CatalogOverviewPage = lazy(() => import('./components/hub/CatalogOverviewPage').then(m => ({ default: m.CatalogOverviewPage })));
const DocsOverviewPage = lazy(() => import('./components/hub/DocsOverviewPage').then(m => ({ default: m.DocsOverviewPage })));
const ResourcesPage = lazy(() => import('./components/hub/ResourcesPage').then(m => ({ default: m.ResourcesPage })));
const CommunityPage = lazy(() => import('./components/hub/CommunityPage').then(m => ({ default: m.CommunityPage })));
const StatusPage = lazy(() => import('./components/status/StatusPage').then(m => ({ default: m.StatusPage })));
const AnnouncementsPage = lazy(() => import('./components/announcements/AnnouncementsPage').then(m => ({ default: m.AnnouncementsPage })));
const McpCatalogPage = lazy(() => import('./components/mcp/McpCatalogPage').then(m => ({ default: m.McpCatalogPage })));

// Partner-world paths. These routes live in ExternalWorld, not in the internal FlatRoutes,
// so an internal user who reaches one directly (a pasted link, a bookmark, or signing in
// while already on it) must be shown the external world — otherwise <Root> renders the
// internal hub, which has no such route, and they hit a branded 404 with no partner content
// and no obvious way on. Reviewers are internal accounts, so this is their normal path in.
const PARTNER_PATH_PREFIXES = ['/products', '/partner-program', '/getting-started', '/contact'];
const isPartnerPath = (pathname: string) =>
  PARTNER_PATH_PREFIXES.some(p => pathname === p || pathname.startsWith(`${p}/`));

// ISDP CUSTOM: what an ANONYMOUS visitor gets at the app root.
//
// '/' is the authentication entry point, which is a dead end for a partner who has no
// Dräger account yet. So when the Partner Portal is part of this deployment, an anonymous
// visitor who lands on '/' is sent to the portal's home (/products) instead of a login
// form they cannot complete.
//
// This lives in the SignInPage slot because that is the one place the app KNOWS the
// visitor is not signed in — AppRouter renders this component exactly then. Four
// conditions keep it from taking the internal hub away from people who want it:
//   - only '/' is redirected; every other path renders the sign-in screen as before;
//   - SIGN_IN_HREF carries ?signin=1, so the portal's own "Sign in" controls opt out
//     (without that the two behaviours contradict and Sign In is a no-op loop);
//   - a browser that has ever resolved an identity here (the devhub-signed-in flag) is
//     left alone, so an internal user returning to '/' still restores their session
//     silently and lands in the internal hub;
//   - on localhost the guest provider signs everyone in automatically, so there is no
//     anonymous visitor to route and dev/e2e behaviour is untouched.
const PortalRootSignIn = (props: SignInPageProps) => {
  const partnerPortal = featureEnabled(useApi(configApiRef), 'partnerPortal');
  const toPortal =
    partnerPortal &&
    !usesGuestAutoSignIn() &&
    window.location.pathname === '/' &&
    !new URLSearchParams(window.location.search).has(SIGN_IN_PARAM) &&
    !isSignedInFlagSet();

  useEffect(() => {
    // replace(), not assign(): '/' must not sit in the history, or Back re-triggers this.
    // A full page load, not a router navigate — /products is served by the public bundle.
    if (toPortal) window.location.replace('/products');
  }, [toPortal]);

  // An EXPIRED session on a public portal path. index.tsx only boots this app for a public
  // path when the devhub-signed-in flag is set, and the flag is cleared only on sign-out —
  // so someone who signed in once and whose session has since lapsed would get the full
  // sign-in screen on a public link (/products…, /getting-started, /contact) that an
  // anonymous visitor reads freely. When the silent session restore comes back empty there,
  // drop the stale flag and reload: index.tsx then boots the anonymous portal on the same
  // URL. Localhost guest mode never gets here (it always signs in).
  const expiredOnPortalPath =
    partnerPortal &&
    !usesGuestAutoSignIn() &&
    isPartnerPath(window.location.pathname) &&
    !new URLSearchParams(window.location.search).has(SIGN_IN_PARAM);

  if (toPortal) return <RoleSplash message="Loading…" />;
  return (
    <DraegerSignInPage
      {...props}
      onNoSession={
        expiredOnPortalPath
          ? () => {
              clearSignedIn();
              window.location.reload();
            }
          : undefined
      }
    />
  );
};

const app = createApp({
  apis,
  // ISDP CUSTOM: Dräger-branded light + dark themes (light listed first).
  // NOTE: do NOT override appThemeApiRef in apis.ts to force light — that crashes
  // createApp in this Backstage version ("duplicate/forbidden API factory").
  // Light-default for custom/ODX pages is handled by the pre-hydration init in
  // index.tsx and PublicShell; core MUI pages fall back to the theme picker.
  themes: appThemes,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  // ISDP CUSTOM: Premium Dräger-branded sign-in. Okta/OIDC in deployed environments,
  // guest sign-in on localhost for dev. See components/SignInPage/SignInPage.tsx.
  components: {
    SignInPage: props => <PortalRootSignIn {...props} />,
    // ISDP CUSTOM: Branded 404 — replaces Backstage's default "dropped the mic" page.
    NotFoundErrorPage: () => <NotFoundPage />,
  },
  // ISDP CUSTOM: i18n. English (default) + German. Custom-page namespaces carry their German
  // inline (see src/i18n/*). `resources` overrides Backstage's own core/plugin refs with German.
  __experimentalTranslations: {
    availableLanguages: ['en', 'de'],
    resources: backstageTranslationResources,
  },
});

// ISDP CUSTOM: The custom catalog table page replaces Backstage's CatalogIndexPage
// at /catalog. Bind the catalogIndex routeRef to it (the way a routable extension
// would) so cross-plugin links — e.g. EntityLayout's "back to catalog" — still resolve.
attachComponentData(CatalogTablePage, 'core.mountPoint', catalogPlugin.routes.catalogIndex);

// ISDP CUSTOM: Authenticated routes inside the Root layout.
// Wrapped in a single Suspense boundary so lazy-loaded route components
// (admin pages, DevSecOps hub) show the RoleSplash spinner while their
// chunk downloads instead of crashing or going blank.
const routes = (
  <Suspense fallback={<RoleSplash message="Loading…" />}>
  <FlatRoutes>
    <Route path={ROUTES.home} element={<HomePage />} />
    {/* ISDP CUSTOM: Section landing/hub pages */}
    <Route path={ROUTES.overview.catalog} element={<CatalogOverviewPage />} />
    <Route path={ROUTES.overview.docs} element={<DocsOverviewPage />} />
    <Route path={ROUTES.overview.resources} element={<ResourcesPage />} />
    <Route path={ROUTES.systemStatus} element={<StatusPage />} />
    {/* ISDP CUSTOM: Self Service = the DevSecOps hub (provided modules), reached directly from the nav.
        User-generated provisioning lives in native Backstage (Catalog/Create), not here. */}
    {/* Open to all internal users (DevSecOpsAuthGate establishes the session; backend scopes data and the
        rail hides admin-only sections). Create = Backstage Scaffolder mounted as a hub tab. */}
    <Route path={ROUTES.selfService.root} element={<DevSecOpsAuthGate><DevSecOpsHubPage /></DevSecOpsAuthGate>} />
    {/* Create (the catalogue + every template wizard) is deliberately NOT behind DevSecOpsAuthGate: any
        internal user can browse it and run a template without a DevSecOps session. The catalogue's
        DevSecOps tool cards come from a bundled snapshot; everything they link into (/self-service…) is
        gated, so the DevSecOps login fires only when a card is opened. */}
    {/* Where the Doc Author Starter Kit's "Open your draft doc" link lands: waits for the catalog to
        pick up the new repo, then opens the doc. More specific than create/*, so it wins the match. */}
    <Route
      path={ROUTES.selfService.docReady}
      element={<HubShell active="create"><DocReadyPage /></HubShell>}
    />
    <Route
      // No trailing /* here: FlatRoutes adds it for matching, but the scaffolder builds its own
      // links (the task page after Create) from this path verbatim — with /* they became
      // /self-service/create/*/tasks/<id>, which 404'd.
      path={ROUTES.selfService.create}
      element={
        <HubShell active="create">
          {/* .devhub-scaffolder scopes the wizard styling (ODX buttons, fields) in fields.css. */}
          <div className="devhub-scaffolder">
            <ScaffolderPage components={{ EXPERIMENTAL_TemplateListPageComponent: CreateTemplateList }}>
              <ScaffolderFieldExtensions>
                <DocTypePickerFieldExtension />
                <AuthorProfileFieldExtension />
                <AdoRepoTargetFieldExtension />
              </ScaffolderFieldExtensions>
            </ScaffolderPage>
          </div>
        </HubShell>
      }
    />
    <Route
      path={`${ROUTES.selfService.root}/projects/:id`}
      element={<DevSecOpsAuthGate><ProjectDetailPage /></DevSecOpsAuthGate>}
    />
    <Route
      path={`${ROUTES.selfService.root}/projects/:id/modules/:mid`}
      element={<DevSecOpsAuthGate><ModuleInstancePage /></DevSecOpsAuthGate>}
    />
    <Route path={ROUTES.overview.community} element={<CommunityPage />} />
    <Route path={ROUTES.catalog.table} element={<CatalogTablePage />} />
    {/* The Curated Catalog page is retired: the Landscape shows the same approved set
        (both read GET /api/curation/landscape), grouped instead of flat. /catalog-curated
        stays as a legacy alias so old links/bookmarks land on the Landscape. */}
    <Route path={ROUTES.catalog.curated} element={<Navigate to={ROUTES.resources.landscape} replace />} />
    <Route path={ROUTES.catalog.cards} element={<CatalogCardPage />} />
    <Route
      path={`${ROUTES.catalog.table}/:namespace/:kind/:name`}
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path={ROUTES.docs.all} element={<DocsPage />} />
    <Route path={ROUTES.docs.curated} element={<CuratedDocsPage />} />
    {/* Bound to the techdocs:index-page routeRef that TechDocsReaderPageHeader
        resolves on every doc page — must stay mounted even though it's not in nav. */}
    <Route path={ROUTES.docs.index} element={<TechDocsIndexPage />} />
    <Route
      path={`${ROUTES.docs.all}/:namespace/:kind/:name/*`}
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        {/* ISDP CUSTOM: the stock ReportIssue addon only supports GitHub/GitLab SCM
            and throws "Invalid SCM type azure" on every doc page (all our docs are
            Azure DevOps repos) — a console error on every Guides/Docs view. We have
            our own "Report a bug" (Sentry) + "View source", so it's removed. */}
        <TechDocsResponsiveStyles />
        <TechDocsCopyPermalink />
        <TechDocsDownloadButton />
        <TechDocsPreviewBanner />
        <TechDocsAuthorTag />
        <TechDocsSourceLink />
        <TechDocsLanguageProxy />
        <Mermaid config={{ theme: 'neutral', themeVariables: { primaryColor: '#e6f0f6' } }} />
      </TechDocsAddons>
    </Route>
    {/* The Scaffolder ("Create") now lives inside the Self Service hub at /self-service/create. */}
    <Route path={ROUTES.create} element={<Navigate to={ROUTES.selfService.create} replace />} />
    {/* ISDP CUSTOM: /api-docs renders the custom card page (card-only, no create button),
        consistent with the rest of the catalog. The stock ApiExplorerPage (grey filter
        sidebar + list/grid toggle + "Register Existing API" button) is retired.
        /api-docs-cards stays as an alias pointing at the same component. */}
    <Route path={ROUTES.api.docs} element={<ApiCardPage />} />
    <Route path={ROUTES.api.cards} element={<ApiCardPage />} />
    <Route
      path={ROUTES.catalog.import}
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path={ROUTES.settings} element={<UserSettingsPage />}>
      {/* ISDP CUSTOM: trimmed settings — a custom ODX General panel replaces the
          stock General/Auth/Feature-Flags tabs; Access Tokens (MCP PATs) stays.
          Wrapping in SettingsLayout bypasses DefaultSettingsPage's stock tabs. */}
      <SettingsLayout>
        <SettingsLayout.Route path="general" title="General">
          <DevHubSettingsGeneral />
        </SettingsLayout.Route>
        <SettingsLayout.Route path="mcp-tokens" title="Access Tokens">
          <McpTokenSettingsTab />
        </SettingsLayout.Route>
      </SettingsLayout>
    </Route>
    {/* ISDP CUSTOM: /catalog-graph has no nav entry and renders an empty canvas on a
        direct hit (it needs a root entity) — minor, since nothing links here. It MUST
        stay bound to <CatalogGraphPage/> though: that page provides the catalog-graph
        routeRef that <EntityCatalogGraphCard/> (on every entity Details tab) resolves
        via useRouteRef. Redirecting this route unbinds the routeRef and crashes every
        entity/API page. Do NOT replace it with a <Navigate/>. */}
    <Route path={ROUTES.catalog.graph} element={<CatalogGraphPage />} />
    {/* ISDP CUSTOM: MCP catalog (discovery). Personal Access Tokens live in the
        external world's API Access page (partners) and Settings → Access Tokens
        (internal); the old standalone /mcp-access route is retired. */}
    <Route path={ROUTES.resources.mcpCatalog} element={<McpCatalogPage />} />
    <Route path={`${ROUTES.resources.mcpCatalog}/:id`} element={<McpCatalogPage />} />
    {/* ISDP CUSTOM: Announcements / DevBlog */}
    <Route path={ROUTES.announcements} element={<AnnouncementsPage />} />
    {/* ISDP CUSTOM: Technology landscape */}
    <Route path={ROUTES.resources.landscape} element={<LandscapePage />} />
    {/* ISDP CUSTOM: Consolidated admin surface — four routed pages, admin only.
        Overview (root) · Analytics [Impact|Usage|AI] · Curation [Content|Radar|MCP]
        · Access (roles + tiers). The Analytics/Curation sub-views are URL-synced
        tabs (?tab=) inside their wrapper pages, not separate routes. */}
    {/* Overview removed — /admin lands on Insights (the default admin section). */}
    <Route path={ROUTES.admin.root} element={<Navigate to={ROUTES.admin.insights} replace />} />
    <Route
      path={ROUTES.admin.insights}
      element={
        <RequirePermission permission={isdpAdminPagePermission}>
          <AnalyticsPage />
        </RequirePermission>
      }
    />
    <Route
      path={ROUTES.admin.curation}
      element={
        <RequirePermission permission={isdpAdminPagePermission}>
          <CurationHubPage />
        </RequirePermission>
      }
    />
    <Route
      path={ROUTES.admin.access}
      element={
        <RequirePermission permission={isdpAdminPagePermission}>
          <AccessManagementPage />
        </RequirePermission>
      }
    />
    <Route
      path={ROUTES.admin.resources}
      element={
        <RequirePermission permission={isdpAdminPagePermission}>
          <ResourceMonitorPage />
        </RequirePermission>
      }
    />
    {/* Back-compat redirects for the pre-consolidation deep links. */}
    <Route path={`${ROUTES.admin.root}/scoreboard`} element={<Navigate to={ROUTES.admin.insights} replace />} />
    <Route path={`${ROUTES.admin.root}/usage`} element={<Navigate to={`${ROUTES.admin.insights}?tab=usage`} replace />} />
    <Route path={`${ROUTES.admin.root}/ai-usage`} element={<Navigate to={`${ROUTES.admin.insights}?tab=ai`} replace />} />
    <Route path={`${ROUTES.admin.root}/tech-radar`} element={<Navigate to={`${ROUTES.admin.curation}?tab=radar`} replace />} />
    <Route path={`${ROUTES.admin.root}/mcp-catalog`} element={<Navigate to={`${ROUTES.admin.curation}?tab=mcp`} replace />} />
  </FlatRoutes>
  </Suspense>
);

// ISDP CUSTOM: Role-aware app body. Runs after Backstage sign-in (identity known).
// Worlds are chosen by ROLE, not by login state (see docs/PARTNER_RESTRUCTURE.md):
//   internal (draeger/admin), not previewing → World B: full internal developer hub
//   internal, previewing                     → World A: external portal, in-app
//   partner / public                         → World A: external Partner Portal, in-app
// No redirects/bounces — the correct world is rendered directly. World A runs in-app
// here (not the standalone PublicApp) so identity + fetchApiRef are available for the
// API Access token page.
//
// RELEASE SCOPE: when the partnerPortal feature is off, World A is not part of this
// deployment. Internal users get the hub exactly as before (useUserRole never reports a
// preview while it is off), and a non-internal account — which cannot exist via the Dräger
// IdP, but must still land somewhere honest — gets the terminal <NoInternalAccess/> screen
// rather than a portal this environment does not serve.

const AppBody = ({ children }: { children: ReactNode }) => {
  const { loading, isInternal, previewing } = useUserRole();
  const partnerPortal = featureEnabled(useApi(configApiRef), 'partnerPortal');
  const location = useLocation();
  // Mounted here — above the routes but inside the router — so both worlds get it, and so it outlives
  // the route elements FlatRoutes unmounts on navigation.
  useScrollRestoration();

  if (loading) {
    return <RoleSplash message="Loading your workspace…" />;
  }

  // An internal user gets the hub — UNLESS they are on a partner path (or previewing), in
  // which case they get the partner world. Internal accounts resolve to premium, so they see
  // full partner content; the shell's "Internal Hub" control and the logo take them back.
  const onPartnerPath = partnerPortal && isPartnerPath(location.pathname);
  if (isInternal && !previewing && !onPartnerPath) {
    return <Root>{children}</Root>;
  }

  if (!partnerPortal) {
    return <NoInternalAccess />;
  }

  // ExternalWorld is lazy-loaded; wrap in its own Suspense (the routes' Suspense only
  // covers the internal <Root> branch, not this partner/public branch).
  return (
    <Suspense fallback={<RoleSplash message="Loading…" />}>
      <ExternalWorld />
    </Suspense>
  );
};

export default app.createRoot(
  <ToastProvider>
    <ConfirmProvider>
      <SentryInit />
      <AlertDisplay />
      <OAuthRequestDialog />
      <AppRouter>
        <DevSecOpsAuthCallback />
        <DevHubErrorBoundary>
          <AppBody>{routes}</AppBody>
        </DevHubErrorBoundary>
      </AppRouter>
    </ConfirmProvider>
  </ToastProvider>,
);
